import type { FloatingPosition } from '@mantine/core';
import { Textarea, Tooltip } from '@mantine/core';
import React, { useEffect, useState } from 'react';
import { MarkdownStepDescription } from './MarkdownStepDescription';

export default function EditableTextArea({
  initialVal,
  placeHolder,
  tooltip,
  tooltipPosition,
  handleSave,
  readonly,
  onEdit,
  onBlur,
  saveOnBlur,
  inputType,
  textStyle,
  inputStyle,
  className,
  maxTextLength,
  // If true, the component will start in edit mode
  startEditing,
  renderMarkdown = false,
}: {
  textStyle?: string;
  initialVal?: string;
  placeHolder?: string;
  tooltip?: string;
  tooltipPosition?: FloatingPosition;
  handleSave: ((newVal: string) => Promise<void>) | ((newVal: string) => void);
  onEdit?: () => void;
  onBlur?: () => void;
  readonly?: boolean;
  saveOnBlur?: boolean;
  inputType?: 'input' | 'textarea';
  inputStyle?: string;
  className?: string;
  maxTextLength?: number;
  startEditing?: boolean;
  renderMarkdown?: boolean;
}) {
  const [value, setValue] = useState(initialVal);
  const [isEditing, setIsEditing] = useState(startEditing || false);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (startEditing) {
      setIsEditing(true);
    }
  }, [startEditing]);

  useEffect(() => setValue(initialVal), [initialVal]);

  const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement> | React.ChangeEvent<HTMLInputElement>) => {
    e.preventDefault();
    setValue(e.target.value);
  };

  const handleBlur = async () => {
    setIsEditing(false);
    onBlur?.();
    if (value !== initialVal && value !== undefined && saveOnBlur) {
      setIsSaving(true);
      await handleSave(value);
      setIsSaving(false);
    }
  };

  const handleKeyDown = async (e: React.KeyboardEvent<HTMLTextAreaElement> | React.KeyboardEvent<HTMLInputElement>) => {
    if (isSaving) {
      return;
    }
    if (e.key === 'Enter') {
      // Command+Enter (Mac) or Ctrl+Enter (Windows/Linux) adds a new line in textarea
      if ((e.metaKey || e.ctrlKey) && inputType === 'textarea') {
        e.preventDefault();
        const { target } = e;
        if (target instanceof HTMLTextAreaElement) {
          const { selectionStart: start, selectionEnd: end } = target;
          const currentValue = value || '';
          const newValue = `${currentValue.substring(0, start)}\n${currentValue.substring(end)}`;
          setValue(newValue);

          // Set cursor position after the inserted newline
          setTimeout(() => {
            if (target instanceof HTMLTextAreaElement) {
              target.selectionStart = target.selectionEnd = start + 1;
            }
          }, 0);
        }
        return;
      }

      // prevent the default action of the enter key
      e.preventDefault();
      // If saveOnBlur, handleBlur will save the value so we don't need to save it again
      if (!saveOnBlur && value !== initialVal && value !== undefined) {
        setIsSaving(true);
        await handleSave(value);
        setIsSaving(false);
      }

      await handleBlur();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      await handleBlur();
    }
  };

  return (
    <div className={`${className ? className : ''} w-full`}>
      {!initialVal || isEditing ?
        inputType === 'textarea' ?
          <Tooltip
            label="⌘+Enter (Ctrl+Enter) to add new line, Enter to save"
            position="top-start"
            color="gray"
            withArrow
            opened={isEditing ? undefined : false}
          >
            <Textarea
              value={value || ''}
              className={`${inputStyle ? inputStyle : ''} w-full`}
              placeholder={placeHolder || 'Enter your value here'}
              onKeyDown={handleKeyDown}
              autosize
              autoFocus={isEditing}
              minRows={1}
              onChange={handleChange}
              onBlur={handleBlur}
            />
          </Tooltip>
        : <input
            value={value}
            className={`w-full rounded-sm bg-transparent py-1 text-sm text-stone-600 focus:outline-none dark:text-stone-300 ${inputStyle || ''}`}
            placeholder={placeHolder || 'Enter your value here'}
            onKeyDown={handleKeyDown}
            autoFocus={isEditing}
            onChange={handleChange}
            onBlur={handleBlur}
          />

      : <Tooltip
          label={tooltip}
          disabled={tooltip === undefined}
          color="gray"
          position={tooltipPosition || 'top-start'}
        >
          <div
            className={`h-full w-full py-1 ${readonly ? 'cursor-default' : 'cursor-text'} break-words ${textStyle || ''}`}
            onClick={() => {
              if (readonly) {
                return;
              }
              setIsEditing(true);
              if (onEdit) {
                onEdit();
              }
            }}
          >
            {renderMarkdown && value !== undefined && value !== '' ?
              <MarkdownStepDescription content={value} />
            : <span className="text-sm font-medium">
                {(value || placeHolder)?.slice(0, maxTextLength ? maxTextLength : (value || placeHolder)?.length) +
                  (maxTextLength !== undefined && maxTextLength < ((value || placeHolder)?.length || 0) ? '...' : '')}
              </span>
            }
          </div>
        </Tooltip>
      }
    </div>
  );
}
